import React, { createContext, useMemo, useState, useEffect, useCallback } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import api, { TOKEN_STORAGE_KEY } from "../api/client";
import { operationalTheme } from './operationalTheme';

const THEME_STORAGE_KEY = "rv-theme";
const THEME_SYNC_EVENT = "rv-theme-sync";

// Available theme modes
export const THEME_MODES = {
  light: { label: "Light Mode", paletteMode: "light" },
  dark: { label: "Dark Mode", paletteMode: "dark" },
  greenBlack: { label: "Green/Black", paletteMode: "dark" },
  redBlack: { label: "Red/Black", paletteMode: "dark" },
};

export const ColorModeContext = createContext({
  mode: "light",
  toggleColorMode: () => {},
  setColorMode: () => {},
});

// Theme palette configurations
const getThemePalette = (mode) => {
  switch (mode) {
    case "greenBlack":
      return {
        mode: "dark",
        primary: { main: "#72e8ac", contrastText: "#082016" },
        secondary: { main: "#a8dbc5" },
        background: {
          default: "#08120f",
          paper: "#101e19",
        },
        text: {
          primary: "#e6f2eb",
          secondary: "#acc5b7",
        },
      };
    case "redBlack":
      return {
        mode: "dark",
        primary: { main: "#ff969c", contrastText: "#2a0d12" },
        secondary: { main: "#e2b7ba" },
        background: {
          default: "#170e11",
          paper: "#25171c",
        },
        text: {
          primary: "#f6e9ec",
          secondary: "#cfb6bd",
        },
      };
    case "dark":
      return {
        mode: "dark",
        primary: { main: "#90caf9" },
        background: {
          default: "#121212",
          paper: "#1d1d1d",
        },
      };
    case "light":
    default:
      return {
        mode: "light",
        primary: { main: "#1e88e5" },
        background: {
          default: "#f7f9fb",
          paper: "#ffffff",
        },
      };
  }
};

export default function ThemeProvider({ children }) {
  const [mode, setMode] = useState("light");

  useEffect(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved && THEME_MODES[saved]) setMode(saved);
  }, []);

  useEffect(() => {
    const handleThemeSync = () => {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      if (saved && THEME_MODES[saved]) {
        setMode(saved);
      } else {
        setMode("light");
      }
    };

    window.addEventListener(THEME_SYNC_EVENT, handleThemeSync);
    return () => window.removeEventListener(THEME_SYNC_EVENT, handleThemeSync);
  }, []);

  const persistThemePreference = useCallback(async (nextMode) => {
    if (typeof window === "undefined") {
      return;
    }
    const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      return;
    }
    try {
      await api.put("/users/preferences", { preferredTheme: nextMode });
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Unable to persist theme preference", error);
      }
    }
  }, []);

  const updateMode = useCallback(
    (nextMode, options = {}) => {
      const { persist = true, syncStorage = true } = options;
      // Validate mode, default to light if invalid
      const normalized = THEME_MODES[nextMode] ? nextMode : "light";
      setMode(normalized);
      if (typeof window !== "undefined" && syncStorage) {
        window.localStorage.setItem(THEME_STORAGE_KEY, normalized);
        window.dispatchEvent(new Event(THEME_SYNC_EVENT));
      }
      if (persist) {
        persistThemePreference(normalized);
      }
    },
    [persistThemePreference]
  );

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => updateMode(mode === "light" ? "dark" : "light"),
      setColorMode: (value, options) => {
        if (!value) {
          return;
        }
        updateMode(value, options);
      },
    }),
    [mode, updateMode],
  );

  const theme = useMemo(
    () =>
      operationalTheme(createTheme({
        palette: getThemePalette(mode),
        shape: { borderRadius: 10 },
      })),
    [mode],
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ColorModeContext.Provider>
  );
}
